import { useEffect, useState, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../context/AuthContext";
import { useModal } from "../../context/ModalContext";
import "./Payment.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "/api";

export default function KakaoPaymentSuccess() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  useModal();
  const history = useHistory();
  const location = useLocation();
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentId = params.get("paymentId");
    // PortOne SDK가 모바일 redirect 시 transactionType, txId 등도 같이 붙여줌
    const code = params.get("code");
    const message = params.get("message");

    if (code) {
      setStatus("error");
      setError(message || `Payment failed (${code})`);
      return;
    }

    if (!paymentId) {
      setStatus("error");
      setError("Missing paymentId");
      return;
    }

    const stored = sessionStorage.getItem("kakao_order_data");
    if (!stored) {
      setStatus("error");
      setError("Order data not found");
      return;
    }

    let parsed;
    try {
      parsed = JSON.parse(stored);
    } catch {
      setStatus("error");
      setError("Invalid order data");
      return;
    }

    const { orderData, grandTotal, shippingCost, itemTotal } = parsed;

    async function processPayment() {
      try {
        // 1. PortOne 결제 검증
        const verifyRes = await fetch(`${API_BASE}/portone/verify-payment`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ paymentId, expectedAmount: Number(grandTotal) }),
        });

        if (!verifyRes.ok) {
          const err = await verifyRes.json().catch(() => ({}));
          setError(err.detail?.message || err.detail?.pgMessage || (typeof err.detail === "string" ? err.detail : "") || err.error || "결제 검증에 실패했습니다.");
          setStatus("error");
          return;
        }

        const verifyData = await verifyRes.json();

        // 2. 주문 등록
        const { quote, formData: formFields } = orderData;
        const items = Array.isArray(quote.items) ? quote.items : [];

        const body = {
          user_id: user?.id || null,
          order_number: quote.orderNum,
          customer_name: formFields.name,
          customer_email: formFields.email,
          recipient_name: formFields.recipientName || formFields.name,
          recipient_phone: formFields.recipientPhone || "",
          shipping_type: formFields.shippingType,
          privacy_agreed: true,
          memo: formFields.memo,
          shipping_cost: shippingCost,
          total_price: itemTotal,
          items,
          portone_payment_id: paymentId,
          portone_tx_id: verifyData.tx_id || null,
          portone_amount_krw: Number(verifyData.paid_amount || grandTotal),
        };

        if (formFields.shippingType === "domestic") {
          body.zipcode = formFields.zipcode;
          body.address = formFields.address;
          body.address_detail = formFields.addressDetail;
          body.delivery_memo = formFields.deliveryMemo;
          body.country = "KR";
        } else {
          body.country = formFields.country;
          body.address = formFields.addressLine1;
          body.address_detail = formFields.addressLine2;
          body.city = formFields.city;
          body.state = formFields.stateProv;
          body.zipcode = formFields.intlZip;
          body.delivery_memo = formFields.intlMemo;
        }

        const orderRes = await fetch(`${API_BASE}/order`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });

        if (!orderRes.ok) {
          const err = await orderRes.json().catch(() => ({}));
          setError(err.error || "Order creation failed");
          setStatus("error");
          return;
        }

        // 3. 주소 저장 옵션
        if (user?.id && formFields.saveAddress) {
          try {
            await fetch(`${API_BASE}/addresses`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                user_id: user.id,
                label: formFields.addressLabel || "기본 배송지",
                is_default: false,
                shipping_type: formFields.shippingType,
                customer_name: formFields.name,
                customer_email: formFields.email,
                zipcode: body.zipcode,
                address: body.address,
                address_detail: body.address_detail,
                country: body.country,
                city: body.city || null,
                state: body.state || null,
                delivery_memo: body.delivery_memo,
                recipient_name: formFields.recipientName || "",
                recipient_phone: formFields.recipientPhone || "",
              }),
            });
          } catch (e) {
            console.error("주소 저장 실패(무시):", e);
          }
        }

        sessionStorage.removeItem("kakao_order_data");
        try { localStorage.removeItem("naverReturn"); } catch (e) { /* noop */ }
        setStatus("success");
      } catch (e) {
        console.error("kakao payment success error", e);
        setError("Payment processing failed");
        setStatus("error");
      }
    }

    processPayment();
  }, []);

  return (
    <div className="payment-result-page">
      <div className="payment-result-card">
        {status === "loading" && (
          <>
            <div className="payment-icon">...</div>
            <h1>{t("payment.processing")}</h1>
            <p>{t("payment.processingDesc")}</p>
          </>
        )}
        {status === "success" && (
          <>
            <div className="payment-icon success">O</div>
            <h1>{t("payment.orderSuccessTitle")}</h1>
            <p>{t("payment.orderSuccessMessage")}</p>
            <button className="btn-primary" onClick={() => history.push("/mypage")}>
              {t("nav.mypage")}
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <div className="payment-icon cancel">X</div>
            <h1>{t("payment.cancelTitle")}</h1>
            <p>{error}</p>
            <button className="btn-primary" onClick={() => history.push("/order")}>
              {t("nav.order", "주문하기")}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
